<script setup>
import { ref, computed, onMounted } from "vue";
import { useTaskStore } from "@/stores/taskStore";
import { useProjectStore } from "@/stores/projectStore";
import TasksModal from "@/components/modal/tasks-modal.vue";
import TasksViewModal from "@/components/modal/tasks-view-modal.vue";
import { Modal } from "bootstrap";
import { useAuthStore } from "@/stores/authStore";

const title = "Tasks";
const text = "Projects";
const text1 = "Tasks";

const auth = useAuthStore();
const taskStore = useTaskStore();
const projectStore = useProjectStore();
const modalRef = ref(null);
const viewModalRef = ref(null);
const exporting = ref("");

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

const isEmployee = computed(() => auth.roles?.includes("Employee"));

const projectOptions = computed(() =>
  (projectStore.items || []).map((p) => ({ id: p.id, label: p.name }))
);

function openCreate() {
  modalRef.value?.openCreate();
}
function openEdit(row) {
  modalRef.value?.openEdit(row.id);
}
function openView(row) {
  viewModalRef.value?.open(row.id);
}

async function openDelete(rec) {
  // 1) Tell the modal which record we plan to delete
  modalRef.value?.openForDelete(rec.id);

  // 2) Show the delete confirmation modal
  const el = document.getElementById("delete_task");
  if (el) {
    new Modal(el, { backdrop: "static", keyboard: false }).show();
  }
}

async function remove(row) {
  if (!confirm("Delete this task?")) return;
  await taskStore.remove(row.id);
  await taskStore.list();
}
async function markComplete(row) {
  if (row.status === "Completed") return;
  await taskStore.complete(row.id);
  await taskStore.list();
}

async function exportTasks(format = "xlsx") {
  try {
    exporting.value = format;
    await taskStore.downloadReport(format);
  } finally {
    exporting.value = "";
  }
}

function reloadFirstPage() {
  taskStore.filters.page = 1;
  taskStore.list({ page: 1 });
}

// let t;
// function debouncedReload() {
//   clearTimeout(t);
//   t = setTimeout(() => taskStore.list(), 250);
// }

let t;
function debouncedReload() {
  clearTimeout(t);
  t = setTimeout(() => reloadFirstPage(), 250);
}

// function setPriority(v) {
//   taskStore.filters.priority = v;
//   taskStore.list();
// }
// function setStatus(v) {
//   taskStore.filters.status = v;
//   taskStore.list();
// }
// function setSort(v) {
//   taskStore.filters.sort = v;
//   taskStore.list();
// }
// function setProject(v) {
//   taskStore.filters.project_id = v;
//   taskStore.list();
// }

function setPriority(v) {
  taskStore.filters.priority = v;
  reloadFirstPage();
}
function setStatus(v) {
  taskStore.filters.status = v;
  reloadFirstPage();
}
function setSort(v) {
  taskStore.filters.sort = v;
  reloadFirstPage();
}
function setProject(v) {
  taskStore.filters.project_id = v;
  reloadFirstPage();
}

// Load more handler
async function loadMore() {
  if (taskStore.loading || !taskStore.hasMore) return;
  const next = (taskStore.filters.page || 1) + 1;
  await taskStore.list({ page: next, append: true });
}

// onMounted(async () => {
//   if (!projectStore.items?.length) await projectStore.list();
//   await taskStore.list({ page: 1 });
// });

onMounted(async () => {
  if (isEmployee.value) taskStore.filters.scope = "me"; // ✅ add
  if (!projectStore.items?.length) await projectStore.list();
  await taskStore.list({ page: 1 });
});
</script>

<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="my-xl-auto right-content d-flex">
          <div class="dropdown me-2 mb-0">
            <a
              href="javascript:void(0);"
              class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
              data-bs-toggle="dropdown"
            >
              <i class="ti ti-file-export me-2"></i>Export
            </a>
            <ul class="dropdown-menu dropdown-menu-end p-3">
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="exportTasks('pdf')"
                >
                  <span
                    v-if="exporting === 'pdf'"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="ti ti-file-type-pdf me-1"></i>Export as PDF
                </a>
              </li>
              <li>
                <a
                  href="javascript:void(0);"
                  class="dropdown-item rounded-1"
                  @click.prevent="exportTasks('xlsx')"
                >
                  <span
                    v-if="exporting === 'xlsx'"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="ti ti-file-type-xls me-1"></i>Export as Excel
                </a>
              </li>
            </ul>
          </div>
          <div class="mb-0">
            <button
              class="btn btn-primary d-flex align-items-center"
              @click="openCreate"
              v-can="'Task.Create'"
            >
              <i class="ti ti-circle-plus me-2"></i>
              Add Task
            </button>
          </div>
          <div class="head-icons ms-2 mb-0">
            <a
              href="javascript:void(0);"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <!-- Filters -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-lg-3 col-sm-6">
              <label class="form-label">Search</label>
              <div class="input-icon position-relative">
                <span class="input-icon-addon"
                  ><i class="ti ti-search"></i
                ></span>
                <input
                  v-model="taskStore.filters.q"
                  @input="debouncedReload"
                  type="text"
                  class="form-control ps-5"
                  placeholder="Search tasks..."
                />
              </div>
            </div>

            <div class="col-lg-3 col-sm-6">
              <label class="form-label">Project</label>
              <select
                class="form-select"
                :value="taskStore.filters.project_id"
                @change="setProject(($event.target.value || '').toString())"
              >
                <option value="">All</option>
                <option v-for="p in projectOptions" :key="p.id" :value="p.id">
                  {{ p.label }}
                </option>
              </select>
            </div>

            <div class="col-lg-2 col-sm-4">
              <label class="form-label mb-4">Priority</label>
              <select
                class="form-select"
                :value="taskStore.filters.priority"
                @change="setPriority($event.target.value)"
              >
                <option value="">All</option>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
            </div>

            <div class="col-lg-2 col-sm-4">
              <label class="form-label">Status</label>
              <select
                class="form-select"
                :value="taskStore.filters.status"
                @change="setStatus($event.target.value)"
              >
                <option value="">All</option>
                <option>Pending</option>
                <option>Inprogress</option>
                <option>Completed</option>
                <option>Onhold</option>
              </select>
            </div>

            <div class="col-lg-2 col-sm-4">
              <label class="form-label">Sort</label>
              <select
                class="form-select"
                :value="taskStore.filters.sort"
                @change="setSort($event.target.value)"
              >
                <option value="created">Created Date</option>
                <option value="due">Due Date</option>
                <option value="priority">Priority</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <!-- /Filters -->

      <!-- Task List -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between"
        >
          <h5 class="mb-0">Task List</h5>
        </div>

        <div class="card-body p-0">
          <div v-if="taskStore.loading" class="p-4 text-center">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading…
          </div>

          <div
            v-else-if="!taskStore.items.length"
            class="p-5 text-center text-muted"
          >
            <img
              class="mb-3 empty-illustration"
              :src="require('@/assets/img/folder-open.svg')"
              alt="empty"
            />
            <div class="fw-medium">No tasks found</div>
            <!-- <div class="small">
              Try adjusting your filters or create a new task.
            </div> -->
            <div>
              <button
                class="btn btn-primary mt-3"
                @click="openCreate"
                v-can="'Task.Create'"
              >
                <i class="ti ti-circle-plus me-2"></i>New Task
              </button>
            </div>
          </div>

          <!-- Rows -->
          <div v-else class="list-group list-group-flush">
            <div
              v-for="row in taskStore.items"
              :key="row.id"
              class="list-group-item p-3 task-row"
            >
              <div class="row g-3 align-items-center">
                <!-- Left: checkbox + title + due -->
                <div
                  class="col-lg-7 col-md-7 d-flex align-items-center flex-wrap"
                >
                  <div class="form-check form-check-md me-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="row.status === 'Completed'"
                      :disabled="row.status === 'Completed'"
                      :aria-label="
                        row.status === 'Completed'
                          ? 'Task completed'
                          : 'Mark task as complete'
                      "
                      @change="markComplete(row)"
                      v-can="'Task.Edit'"
                    />
                  </div>

                  <h6
                    class="mb-0 me-2 text-truncate task-title"
                    :class="{ 'is-completed': row.status === 'Completed' }"
                    :title="row.title"
                  >
                    {{ row.title }}
                  </h6>

                  <span
                    v-if="row.due_pretty"
                    class="badge badge-date ms-2"
                    :class="{
                      'badge-danger-soft':
                        row.status !== 'Completed' && row.overdue,
                      'badge-dark-soft': !row.overdue,
                    }"
                  >
                    <i class="ti ti-calendar me-1"></i>{{ row.due_pretty }}
                  </span>
                </div>

                <!-- Right: three labeled blocks -->
                <div
                  class="col-lg-5 col-md-5 d-flex justify-content-md-end flex-wrap gap-4"
                >
                  <!-- Priority block -->
                  <div class="mini-block">
                    <div class="mini-title">Priority</div>
                    <div>
                      <span
                        class="badge priority-pill"
                        :class="{
                          'priority-high': row.priority === 'High',
                          'priority-medium': row.priority === 'Medium',
                          'priority-low': row.priority === 'Low',
                        }"
                      >
                        {{ row.priority }}
                      </span>
                    </div>
                  </div>

                  <!-- Status block -->
                  <div class="mini-block">
                    <div class="mini-title">Status</div>
                    <div>
                      <span
                        class="badge status-pill d-inline-flex align-items-center"
                        :class="{
                          'status-completed': row.status === 'Completed',
                          'status-pending': row.status === 'Pending',
                          'status-progress': row.status === 'Inprogress',
                          'status-onhold': row.status === 'Onhold',
                        }"
                      >
                        <i class="fas fa-circle fs-6 me-1"></i>{{ row.status }}
                      </span>
                    </div>
                  </div>

                  <!-- Assignees block -->
                  <div class="mini-block">
                    <div class="mini-title">Assignees</div>
                    <div class="d-flex align-items-center">
                      <div class="avatar-list-stacked avatar-group-sm">
                        <span
                          v-for="(av, i) in (row.assignee_avatars || []).slice(
                            0,
                            3
                          )"
                          :key="i"
                          class="avatar avatar-rounded"
                        >
                          <img
                            class="border border-white"
                            :src="
                              av ||
                              require('@/assets/img/profiles/avatar-02.jpg')
                            "
                            alt="assignee"
                          />
                        </span>
                        <a
                          v-if="(row.assignee_extra || 0) > 0"
                          class="avatar bg-primary avatar-rounded text-fixed-white fs-12 fw-medium"
                        >
                          +{{ row.assignee_extra }}
                        </a>
                      </div>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="dropdown align-self-start">
                    <a
                      href="javascript:void(0);"
                      class="d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i class="ti ti-dots-vertical"></i>
                    </a>
                    <ul class="dropdown-menu dropdown-menu-end p-3">
                      <li>
                        <a
                          href="javascript:void(0);"
                          class="dropdown-item rounded-1"
                          @click="openView(row)"
                          v-can="'Task.Read'"
                        >
                          <i class="ti ti-eye me-2"></i>View
                        </a>
                      </li>
                      <li>
                        <a
                          href="javascript:void(0);"
                          class="dropdown-item rounded-1"
                          @click="openEdit(row)"
                          v-can="'Task.Edit'"
                        >
                          <i class="ti ti-edit me-2"></i>Edit
                        </a>
                      </li>
                      <li>
                        <a
                          href="javascript:void(0);"
                          class="dropdown-item rounded-1"
                          @click="openDelete(row)"
                          v-can="'Task.Delete'"
                        >
                          <i class="ti ti-trash me-2"></i>Delete
                        </a>
                        <!-- <button
                          class="dropdown-item rounded-1 d-flex align-items-center"
                          @click="remove(row)"
                          :disabled="taskStore.deletingId === row.id"
                        >
                          <span
                            v-if="taskStore.deletingId === row.id"
                            class="spinner-border spinner-border-sm me-1"
                          ></span>
                          <i v-else class="ti ti-trash me-2"></i>Delete
                        </button> -->
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Meta row -->
              <div class="row mt-2 small text-muted meta-row">
                <div
                  v-if="row.project_name"
                  class="col-auto d-flex align-items-center"
                >
                  <i class="ti ti-briefcase me-1"></i> {{ row.project_name }}
                </div>
                <div
                  v-if="row.tags?.length"
                  class="col-auto d-flex align-items-center flex-wrap gap-1"
                >
                  <i class="ti ti-tag me-1"></i>
                  <span v-for="t in row.tags" :key="t" class="badge tag-pill">{{
                    t
                  }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- /Rows -->

          <div class="col-md-12" v-if="taskStore.items.length">
            <div class="text-center mb-4">
              <button
                class="btn btn-primary"
                :disabled="taskStore.loading || !taskStore.hasMore"
                @click="loadMore"
              >
                <span
                  v-if="taskStore.loading"
                  class="spinner-border spinner-border-sm me-1"
                ></span>
                <i v-else class="ti ti-loader-3 me-1"></i>
                <span v-if="taskStore.hasMore">Load More</span>
                <span v-else>All Loaded</span>
              </button>
              <div class="small text-muted mt-2">
                Showing {{ taskStore.items.length }} of
                {{ taskStore.meta.total || taskStore.items.length }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- /Task List -->
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>

  <tasks-modal ref="modalRef" />
  <tasks-view-modal ref="viewModalRef" />
</template>

<style scoped>
/* --- Row polish --- */
.task-row {
  border-left: 4px solid transparent;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    transform 0.08s ease;
  border-bottom: 1px solid var(--bs-border-color);
}
.task-row:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  border-left-color: var(--bs-primary);
} /* Completed title style (strike through + softened) */
.task-title.is-completed {
  text-decoration: line-through;
  opacity: 0.65;
} /* Date badge */
.badge-date {
  border: 1px dashed var(--bs-secondary);
  background: #f8f9fa;
  color: #333;
}
.badge-danger-soft {
  background: #fff2f2;
  border-color: #ffd6d6;
  color: #b42318;
}
.badge-dark-soft {
  background: #f1f3f5;
  border-color: #e9ecef;
  color: #212529;
} /* Priority chips */
.priority-pill {
  border: 1px solid transparent;
  background: #f8f9fa;
}
.priority-high {
  border-color: #ffd1d1;
  background: #fff2f2;
  color: #c1121f;
}
.priority-medium {
  border-color: #ffe3a3;
  background: #fff8e1;
  color: #ad6800;
}
.priority-low {
  border-color: #b7f0d0;
  background: #e9fff4;
  color: #13795b;
} /* Status chips */
.status-pill {
  border: 1px solid transparent;
}
.status-completed {
  background: #eaf7ef;
  border-color: #c7efd6;
  color: #1b6f3c;
}
.status-pending {
  background: #f2f4f7;
  border-color: #e5e7eb;
  color: #424b57;
}
.status-progress {
  background: #e8f3ff;
  border-color: #cfe8ff;
  color: #0a66c2;
}
.status-onhold {
  background: #fff1f5;
  border-color: #ffd3e2;
  color: #b81e5b;
} /* Tag pills */
.tag-pill {
  background: #f4f6f8;
  color: #333;
  border: 1px solid #e9ecef;
} /* Empty state art */
.empty-illustration {
  max-width: 180px;
  opacity: 0.85;
} /* Tighter meta row */
.meta-row .ti {
  opacity: 0.7;
} /* Input with icon */
.input-icon .input-icon-addon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  color: var(--bs-gray-600);
}
.input-icon .form-control.ps-5 {
  padding-left: 2.3rem !important;
} /* Slightly denser list */
.list-group-item {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}
</style>


